import * as React from 'react'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import {DateRangePicker as DateRangePickerLib} from 'react-date-range'
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state'
import {Box, Button, IconButton, InputAdornment, Popover, TextField, Typography} from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CheckIcon from '@mui/icons-material/Check'
import {formatDate1} from '../common/util'
import {format, isValid} from 'date-fns'

function formatRangeLabel(dateRange) {
  if (!dateRange?.length) return 'Select date range'
  const {startDate, endDate} = dateRange[0]
  const start = startDate ? new Date(startDate) : null
  const end   = endDate   ? new Date(endDate)   : null
  if (!start || !isValid(start)) return 'Select date range'
  const startYear = format(start, 'yyyy')
  const endYear   = (end && isValid(end)) ? format(end, 'yyyy') : startYear
  const startStr  = format(start, startYear === endYear ? 'dd MMM' : 'dd MMM yyyy')
  const endStr    = (end && isValid(end)) ? format(end, 'dd MMM yyyy') : ''
  return endStr ? `${startStr} – ${endStr}` : startStr
}

export default function DateRangePicker(props) {
  const {onChange, defaultRange, showTextBox = false, hideOkButton = false, size, ...other} = props

  const defaultDateRange = (defaultRange?.length > 0)
    ? defaultRange
    : [{startDate: null, endDate: null, key: 'selection'}]

  const [dateRange, setDateRange] = React.useState(defaultDateRange)

  const handleDateRangeChange = (dates) => {
    setDateRange([dates.selection])
    if (onChange instanceof Function && hideOkButton) {
      onChange([dates.selection])
    }
  }

  React.useEffect(() => {
    if (defaultRange?.length > 0) {
      setDateRange(defaultRange)
    } else {
      setDateRange([{startDate: null, endDate: null, key: 'selection'}])
    }
  }, [defaultRange])

  // Legacy val for showTextBox mode
  let val = ''
  if (dateRange.length > 0) {
    if (dateRange[0].startDate) val += formatDate1(dateRange[0].startDate, 'dd MMM yyyy')
    if (dateRange[0].endDate)   val += ' – ' + formatDate1(dateRange[0].endDate, 'dd MMM yyyy')
  }

  const rangeLabel = formatRangeLabel(dateRange)

  return (
    <PopupState variant='popover' popupId='date-range-selector'>
      {(popupState) => (
        <React.Fragment>
          {showTextBox ? (
            <TextField
              onClick={popupState.open}
              readOnly
              value={val}
              variant='outlined'
              fullWidth
              size={size || 'medium'}
              label={props.label === '' ? undefined : (props.label || 'Select Range')}
              placeholder={props.placeholder}
              InputLabelProps={{style: {fontSize: 14}, shrink: props.placeholder ? true : undefined}}
              inputProps={{ placeholder: props.placeholder }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton {...bindTrigger(popupState)}>
                      <CalendarMonthIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Button
              variant='outlined'
              size={size || 'small'}
              startIcon={<CalendarMonthIcon sx={{fontSize: '16px !important', color: '#1565c0'}} />}
              {...bindTrigger(popupState)}
              sx={{
                borderColor: '#c5d8f5',
                backgroundColor: '#f5f9ff',
                color: '#1565c0',
                fontWeight: 600,
                fontSize: '0.78rem',
                textTransform: 'none',
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#e8f0fd',
                  boxShadow: 'none',
                },
                ...props.buttonSx,
              }}
            >
              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1}}>
                <Typography sx={{fontSize: '0.65rem', color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1}}>
                  Date Range
                </Typography>
                <Typography sx={{fontSize: '0.78rem', fontWeight: 700, color: '#1565c0', lineHeight: 1.3}}>
                  {rangeLabel}
                </Typography>
              </Box>
            </Button>
          )}

          <Popover
            {...bindMenu(popupState)}
            PaperProps={{
              sx: {
                borderRadius: 2.5,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid #e8e8e8',
                overflow: 'hidden',
              },
            }}
          >
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
              <DateRangePickerLib
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={2}
                direction='horizontal'
                ranges={dateRange}
                maxDate={props.maxDate || undefined}
                onChange={handleDateRangeChange}
                dateDisplayFormat='MMM d, yyyy'
                {...other}
              />
              {!hideOkButton && (
                <Box sx={{px: 2, pb: 2, pt: 0.5, width: '100%', borderTop: '1px solid #f0f0f0'}}>
                  <Button
                    onClick={() => {
                      if (onChange instanceof Function) onChange(dateRange)
                      popupState.close()
                    }}
                    startIcon={<CheckIcon />}
                    variant='contained'
                    color='primary'
                    size='small'
                    sx={{borderRadius: 1.5, textTransform: 'none', fontWeight: 600}}
                  >
                    Apply
                  </Button>
                </Box>
              )}
            </Box>
          </Popover>
        </React.Fragment>
      )}
    </PopupState>
  )
}
